import { useEffect, useState } from 'react';

/**
 * The current instant, updated once a minute.
 *
 * One interval for the whole page, held by the root and passed down, so the
 * clock and the greeting read the same moment — a greeting that only updated on
 * reload would still say "Good morning" at half past two.
 *
 * The interval still POLLS once a second, so a minute rollover is caught
 * within a second of it happening — but state is only set when the minute
 * bucket actually changes. Nothing on the page displays seconds and the
 * greeting only flips on the hour, so the finest granularity anything renders
 * is one minute; setting state every second re-rendered the whole tree (tiles,
 * access panel, every chip in it) sixty times for one visible change. A
 * timezone's minute boundaries land on the same instant as every other
 * timezone's, so this stays exact for the greeting even though it is read in
 * the user's own zone rather than the browser's.
 *
 * A backgrounded tab has its timers throttled, so it comes back with a reading
 * minutes stale. Re-syncing on the way back in is cheaper and more reliable than
 * trying to keep the interval accurate while hidden — and always sets state,
 * bucket check aside, so a resync after being hidden is never stale by up to a
 * minute.
 */
const minuteOf = (date) => Math.floor(date.getTime() / 60000);

export default function useNow() {
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        let lastMinute = minuteOf(new Date());

        const tick = () => {
            const next = new Date();

            if (minuteOf(next) !== lastMinute) {
                lastMinute = minuteOf(next);
                setNow(next);
            }
        };

        const interval = setInterval(tick, 1000);

        const resync = () => {
            if (!document.hidden) {
                const next = new Date();
                lastMinute = minuteOf(next);
                setNow(next);
            }
        };

        document.addEventListener('visibilitychange', resync);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', resync);
        };
    }, []);

    return now;
}
